import { useEffect, useState } from 'react';
import { RichText, toPlainText } from '../../components/rich-text';
import { Dialog, Pagination, RunItemStatusBadge, SkeletonRows } from '../../components/ui';
import { useFormat, useT } from '../../i18n';
import { useRunItemResults } from './api';

/** A long history would otherwise make the dialog taller than the screen. */
const PER_PAGE = 5;

/**
 * Execution history of a single check. Results are append-only, so this is the
 * full record of who set which status and when.
 */
export const RunItemHistoryDialog = ({
  projectCode,
  runId,
  item,
  onClose,
}: {
  projectCode: string;
  runId: string;
  item: { id: string; position: number; title: string } | null;
  onClose: () => void;
}) => {
  const { t } = useT();
  const { dateTime } = useFormat();
  const { data, isPending } = useRunItemResults(projectCode, runId, item?.id ?? null);
  const [page, setPage] = useState(1);

  // Each check opens on its newest result, whatever page the last one ended on.
  useEffect(() => setPage(1), [item?.id]);

  const results = data ?? [];
  const totalPages = Math.max(1, Math.ceil(results.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const visible = results.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  return (
    <Dialog
      open={item !== null}
      onOpenChange={(open) => !open && onClose()}
      title={item ? t('runs.historyTitle', { position: item.position }) : t('runs.history')}
      description={item ? toPlainText(item.title) : undefined}
      size="sm"
    >
      {isPending ? (
        <SkeletonRows rows={3} className="p-0" />
      ) : (data ?? []).length === 0 ? (
        <p className="text-[13px] text-ink-subtle">{t('runs.historyEmpty')}</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {visible.map((result) => (
            <li key={result.id} className="border-l-2 border-border pl-3">
              <div className="flex items-center gap-2">
                <RunItemStatusBadge status={result.status} />
                <span className="text-[12px] text-ink-subtle">
                  {dateTime(result.executedAt)}
                </span>
              </div>
              <p className="mt-1 text-[13px] text-ink-muted">
                {t('runs.historyBy', { name: result.executedBy?.name ?? t('common.empty') })}
              </p>
              {result.comment ? <RichText value={result.comment} className="mt-1" /> : null}
            </li>
          ))}
        </ol>
      )}

      {results.length > PER_PAGE ? (
        <Pagination
          meta={{
            page: current,
            perPage: PER_PAGE,
            total: results.length,
            totalPages,
          }}
          onPageChange={setPage}
        />
      ) : null}
    </Dialog>
  );
};
